import { type AdminSiteSettings, kakaoChannelChatUrlOf } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { formatDateTime } from '@/shared/lib';
import { Button, FormField } from '@/shared/ui';
import { parseKakaoChannelForm } from '../model/kakao-channel-form';
import { useUpdateSiteSettings } from '../model/use-update-site-settings';

export interface KakaoChannelFormProps {
  readonly settings: AdminSiteSettings;
}

const NOTICE =
  "주소를 넣고 저장하면 고객용 웹 모든 화면 오른쪽 아래에 '카카오톡 상담' 버튼이 생기고, 누르면 채널 1:1 채팅이 새 탭으로 열립니다. 재배포 없이 바로 적용됩니다.";

/**
 * 카카오톡 상담 버튼 폼(운영 설정 설계 K2·K4) — 채널 주소 한 칸, [저장], 저장된 주소의 채팅 확인 링크, 마지막 변경 시각·변경자.
 * 저장 뒤 새 설정이 오면 상위가 key 로 다시 시작한다. 서버가 거부하면 입력을 그대로 두고 문구를 보인다.
 */
export const KakaoChannelForm = ({ settings }: KakaoChannelFormProps): ReactElement => {
  const initial = settings.kakaoChannelUrl ?? '';
  const [value, setValue] = useState(initial);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const { update, isPending, error } = useUpdateSiteSettings();
  const isDirty = value.trim() !== initial;
  const updatedAt = settings.updatedAt ? formatDateTime(settings.updatedAt) : '-';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const parsed = parseKakaoChannelForm(value);
    if (!parsed.ok) {
      setFieldError(parsed.error);
      return;
    }
    setFieldError(undefined);
    await update(parsed.input);
  };

  return (
    <form
      aria-label="카카오톡 상담 버튼"
      noValidate
      onSubmit={(event) => void handleSubmit(event)}
      className="flex max-w-xl flex-col gap-4"
    >
      <FormField
        label="카카오톡 채널 주소"
        description="카카오 채널 관리자에 나오는 채널 주소(https://pf.kakao.com/_채널ID). 비우고 저장하면 버튼이 사라집니다."
        placeholder="https://pf.kakao.com/_abc123"
        inputMode="url"
        autoComplete="off"
        value={value}
        error={fieldError}
        disabled={isPending}
        onChange={(event) => setValue(event.target.value)}
      />
      {settings.kakaoChannelUrl ? (
        <a
          href={kakaoChannelChatUrlOf(settings.kakaoChannelUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="self-start text-sm underline"
        >
          채팅 열어 보기
        </a>
      ) : null}
      <p className="text-sm text-muted-foreground">{NOTICE}</p>
      <p className="text-sm text-muted-foreground">
        {`마지막 변경 ${updatedAt} · ${settings.updatedBy?.name ?? '-'}`}
      </p>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" isLoading={isPending} disabled={!isDirty} className="self-start">
        저장
      </Button>
    </form>
  );
};
